import { Icon } from '@natera/material/lib/icon'
import { useCallback, useMemo, useState } from 'react'
import { getDisplayName, USIndicator } from '~/entities/reviewer'
import type { apiClient } from '~/shared/api/data-ingestion-api'
import { highlightText } from '~/shared/lib/highlight-text'
import {
  LocalIcon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RadioButton,
  SearchView,
  Text,
} from '~/shared/ui'

export function ReviewerFilter({
  reviewers,
  reviewerEmails,
  setReviewerEmails,
}: {
  reviewers: apiClient.User[]
  reviewerEmails: string[]
  setReviewerEmails: (reviewerEmails: string[]) => void
}) {
  const [searchValue, setSearchValue] = useState('')

  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value)
  }, [])

  const filteredReviewers = useMemo(() => {
    return reviewers.filter((reviewer) => {
      return (
        reviewer.first_name?.toLowerCase().includes(searchValue.toLowerCase()) ||
        reviewer.last_name?.toLowerCase().includes(searchValue.toLowerCase())
      )
    })
  }, [reviewers, searchValue])

  const visibleReviewers = useMemo(() => {
    if (searchValue) {
      return filteredReviewers
    }
    return reviewers
  }, [filteredReviewers, reviewers, searchValue])

  const isOptiVisible = useMemo(() => {
    return !searchValue || 'opti'.includes(searchValue.toLowerCase())
  }, [searchValue])

  return (
    <Popover>
      <PopoverTrigger className="bg-surface-lowest data-[state=open]:bg-action-hovered hover:bg-action-hovered flex h-12 w-[120px] cursor-pointer items-center justify-between gap-2 rounded-sm border-none px-3 transition-colors">
        <Text
          className="text-text-primary"
          appearance="BODY2"
        >
          Reviewer
        </Text>
        <Icon
          children="arrow_drop_down"
          className="text-content-high-emphasis"
        />
      </PopoverTrigger>
      <PopoverContent align="start">
        <SearchView
          className="w-[240px]"
          value={searchValue}
          onChange={handleSearchChange}
          placeholder="Search"
          forceOpen
        >
          {visibleReviewers.length > 0 || isOptiVisible ? (
            <>
              <Text
                className="box-border w-full px-4 pt-3 pb-1"
                appearance="SUBTITLE"
              >
                Reviewer
              </Text>

              {isOptiVisible ? (
                <div className="border-divider flex w-full items-center gap-2 border-b px-2 py-1.5">
                  <RadioButton
                    className="w-full"
                    label={
                      <div className="flex w-full items-center justify-between gap-3 pl-1 whitespace-normal">
                        <Text appearance="BODY2">{highlightText('opti', searchValue)}</Text>
                        <LocalIcon
                          className="text-text-secondary mr-3"
                          name="opti"
                        />
                      </div>
                    }
                    fullLabelWidth
                    gutter
                    size="small"
                    checked={reviewerEmails.includes('opti')}
                    onChange={() => {
                      setReviewerEmails(['opti'])
                    }}
                  />
                </div>
              ) : null}

              {visibleReviewers.map((reviewer) => (
                <div
                  className="border-divider flex w-full items-center gap-2 border-b px-2 py-1.5"
                  key={reviewer.sub}
                >
                  <RadioButton
                    className="w-full"
                    label={
                      <div className="flex w-full items-center justify-between gap-3 pl-1 whitespace-normal">
                        <Text appearance="BODY2">
                          {highlightText(getDisplayName(reviewer), searchValue)}
                        </Text>
                        {!reviewer.is_offshore_user && (
                          <USIndicator
                            className="mr-2"
                            tooltip="US Reviewer"
                          />
                        )}
                      </div>
                    }
                    fullLabelWidth
                    gutter
                    size="small"
                    checked={reviewerEmails.includes(reviewer.sub)}
                    onChange={() => {
                      setReviewerEmails([reviewer.sub])
                    }}
                  />
                </div>
              ))}
            </>
          ) : (
            <Text
              className="box-border w-full px-4 py-3"
              appearance="SUBTITLE"
            >
              No Results
            </Text>
          )}
        </SearchView>
      </PopoverContent>
    </Popover>
  )
}
